/**
 * The Go box: a visible input at the top of the rail — "Go to, or ask…" —
 * whose dropdown tells you what kind of thing you are about to do before you
 * click: destinations, then Search everywhere, then Ask Conway (nav spec §4.4).
 * Enter and the arrows are accelerators; every row is a click target.
 *
 * Collapsed, it is a ⌕ row that expands the rail and then focuses the input —
 * a visible button, not a hidden affordance.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';

import { useUI } from '@/stores/useAppStore';
import { askConway } from '@features/chat/services/askConway';
import { useWorkspaceNav } from '@features/workspace/hooks/useWorkspaceNav';
import { useActiveContext } from '@shared/active-context';
import { chromeFor } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { DomainIcon, Icons } from '@shared/components/ui';
import { Z } from '@shared/constants/zIndex';
import { recordGoPick } from '@shared/navigation/goPicks';

import SideNavRowButton from './SideNavRowButton';
import { useGoBoxRows } from './useGoBoxRows';

import type { GoBoxRow } from './goBoxRows';

interface GoBoxProps {
  isCollapsed: boolean;
  /** Called after any action, e.g. so a host can close itself. */
  onNavigate?: () => void;
}

const PLACEHOLDER = 'Go to, or ask…';

/** The dropdown never gets narrower than this, even on a narrow rail. */
const MIN_LIST_WIDTH = 260;

/** The search page URL for a query; `scope=all` is explicit because a missing scope means email. */
function searchPath(query: string): string {
  return `/search?${new URLSearchParams({ q: query.trim(), scope: 'all' }).toString()}`;
}

/**
 * A row's icon, main text and trailing hint.
 *
 * @param row - The row to render.
 * @param query - The current Go-box text; shown as the Search/Ask hint.
 * @param hasSelection - Whether the context object holds a retained selection —
 *   named on the Ask row so including it is stated, not a hidden affordance.
 */
function rowContent(
  row: GoBoxRow,
  query: string,
  hasSelection: boolean
): { icon: React.ReactNode; text: string; hint?: string } {
  switch (row.kind) {
    case 'destination': {
      const d = row.destination;
      const Glyph = Icons[d.icon];
      const icon = d.domain ? (
        <DomainIcon domain={d.domain} size="sm" />
      ) : (
        <Glyph size="sm" color="inherit" />
      );
      return { icon, text: d.label, hint: row.guess ? 'Best guess' : d.description };
    }
    case 'search':
      return {
        icon: <Icons.Search size="sm" color="inherit" />,
        text: `Search everywhere for "${query.trim()}"`,
        hint: '→',
      };
    case 'ask':
      return {
        icon: <Icons.Sparkles size="sm" color="inherit" />,
        text: hasSelection ? 'Ask Conway about the selection' : 'Ask Conway',
        hint: query.trim(),
      };
  }
}

/** A stable key per row: destination id, or the kind for the two fixed rows. */
function rowKey(row: GoBoxRow): string {
  return row.kind === 'destination' ? row.destination.id : row.kind;
}

/** Collapsed rail: the ⌕ row that widens the rail. */
const CollapsedGoBox: React.FC<{ onExpand: () => void }> = ({ onExpand }) => (
  <SideNavRowButton
    isCollapsed
    icon="Search"
    domain="search"
    label={PLACEHOLDER}
    ariaLabel="Go to, or ask"
    inactiveText="text-sidebar-foreground/80"
    hover="hover:text-sidebar-foreground"
    onClick={onExpand}
  />
);

interface GoBoxListProps {
  rows: GoBoxRow[];
  query: string;
  /** Whether the context object holds a retained selection; named on the Ask row. */
  hasSelection: boolean;
  activeIndex: number;
  listId: string;
  onHighlight: (index: number) => void;
  onActivate: (row: GoBoxRow) => void;
}

/** Heads the out-of-app rows inside an installed app, so a jump out of the app is stated. */
const ElsewhereLabel: React.FC = () => (
  <li role="presentation" className="text-muted-foreground px-3 pt-2 pb-1 text-xs">
    Elsewhere in Conway
  </li>
);

/** Says a best guess is on its way, so the rows changing under the finger are expected. */
const GuessingLine: React.FC = () => (
  <li role="presentation" className="text-muted-foreground px-3 py-1.5 text-xs">
    Finding the right page…
  </li>
);

/** True for the first `elsewhere` destination row: the one the label goes above. */
function startsElsewhere(rows: GoBoxRow[], i: number): boolean {
  const isElsewhere = (r: GoBoxRow | undefined) => r?.kind === 'destination' && r.elsewhere;
  return isElsewhere(rows[i]) && !isElsewhere(rows[i - 1]);
}

/** The result rows. Keyboard lives on the combobox input; each row is a click target. */
const GoBoxList: React.FC<GoBoxListProps> = ({
  rows,
  query,
  hasSelection,
  activeIndex,
  listId,
  onHighlight,
  onActivate,
}) => (
  <>
    {rows.map((row, i) => {
      const { icon, text, hint } = rowContent(row, query, hasSelection);
      const active = i === activeIndex;
      return (
        <React.Fragment key={rowKey(row)}>
          {startsElsewhere(rows, i) && <ElsewhereLabel />}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard handled on the combobox input; the row is the click target */}
          <li
            id={`${listId}-${i}`}
            role="option"
            aria-selected={active}
            onMouseEnter={() => onHighlight(i)}
            // Keep focus on the input so useDismiss does not close the list before the click lands.
            onMouseDown={e => e.preventDefault()}
            onClick={() => onActivate(row)}
            className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${active ? 'bg-muted text-foreground' : 'text-foreground'}`}
          >
            <span className="text-muted-foreground flex w-5 shrink-0 justify-center">{icon}</span>
            <span className="min-w-0 flex-1 truncate">{text}</span>
            {hint && (
              <span className="text-muted-foreground max-w-[45%] truncate text-xs">{hint}</span>
            )}
          </li>
        </React.Fragment>
      );
    })}
  </>
);

const GoBox: React.FC<GoBoxProps> = ({ isCollapsed, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rawActiveIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusAfterExpandRef = useRef(false);
  const listId = useId();
  const { setSidebarCollapsed } = useUI();
  const installedApp = useInstalledApp();
  const { openDestination, openPath } = useWorkspaceNav();
  const { selection } = useActiveContext();

  const showAsk = installedApp === null || chromeFor(installedApp).showsAsk;
  const { rows, guessing } = useGoBoxRows(query, open, { installedApp, showAsk });
  const showList = open && rows.length > 0;
  // Clamped at read time so the highlight stays inside the list as the rows change under it.
  const activeIndex = Math.min(rawActiveIndex, Math.max(0, rows.length - 1));

  // Destructured: the React Compiler reads `refs.x` under a conditional as a
  // ref access and skips the whole component.
  const {
    refs: { setFloating, setPositionReference, setReference },
    floatingStyles,
    context,
  } = useFloating({
    open: showList,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${Math.max(rects.reference.width, MIN_LIST_WIDTH)}px`,
          });
        },
      }),
    ],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);
  const inputRefs = useMergeRefs([inputRef, setReference]);

  // The collapsed ⌕ row asked for the input; focus it once the rail has widened.
  useEffect(() => {
    if (!isCollapsed && focusAfterExpandRef.current) {
      focusAfterExpandRef.current = false;
      inputRef.current?.focus();
    }
  }, [isCollapsed]);

  const reset = () => {
    setQuery('');
    setOpen(false);
    setActiveIndex(0);
  };

  const activate = (row: GoBoxRow) => {
    const text = query;
    reset();
    if (row.kind === 'destination') {
      recordGoPick(text, row.destination.id);
      openDestination(row.destination.id);
    } else if (row.kind === 'search') openPath('Search', searchPath(text));
    else askConway(text, () => openPath('Chat', '/chat'), selection);
    onNavigate?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && rows.length) {
      e.preventDefault();
      if (!showList) {
        setOpen(true);
        return;
      }
      setActiveIndex(Math.min(activeIndex + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp' && rows.length) {
      e.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter' && showList) {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) activate(row);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (isCollapsed) {
    return (
      <CollapsedGoBox
        onExpand={() => {
          focusAfterExpandRef.current = true;
          setSidebarCollapsed(false);
        }}
      />
    );
  }

  return (
    <div className="px-1">
      <div
        ref={setPositionReference}
        className="bg-sidebar-accent/60 focus-within:bg-sidebar-accent flex h-8 items-center gap-2 rounded-md px-2"
      >
        <Icons.Search size="sm" color="inherit" className="text-sidebar-foreground/60 shrink-0" />
        <input
          ref={inputRefs}
          {...getReferenceProps({
            value: query,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIndex(0);
            },
            onKeyDown,
            onFocus: () => setOpen(true),
            // A click on an already-focused input reopens a list closed with Escape.
            onClick: () => setOpen(true),
          })}
          type="text"
          role="combobox"
          aria-label="Go to, or ask"
          aria-expanded={showList}
          aria-controls={listId}
          {...(showList ? { 'aria-activedescendant': `${listId}-${activeIndex}` } : {})}
          aria-autocomplete="list"
          placeholder={PLACEHOLDER}
          className="text-sidebar-foreground placeholder:text-sidebar-foreground/50 min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
      </div>
      {showList && (
        <FloatingPortal>
          <ul
            ref={setFloating}
            style={{ ...floatingStyles, zIndex: Z.DROPDOWN }}
            {...getFloatingProps()}
            id={listId}
            className="border-border bg-card max-h-80 overflow-y-auto rounded-md border py-1 shadow-lg"
          >
            {guessing && <GuessingLine />}
            <GoBoxList
              rows={rows}
              query={query}
              hasSelection={selection !== null}
              activeIndex={activeIndex}
              listId={listId}
              onHighlight={setActiveIndex}
              onActivate={activate}
            />
          </ul>
        </FloatingPortal>
      )}
    </div>
  );
};

export default GoBox;
